import { useEffect } from "react";

import type { Attendee } from "../components/types";

type Mode =
  | { type: "list" }
  | { type: "add" }
  | { type: "replace"; targetPlayerId: string };

type InitialSnapshot = {
  ownerId: string | null;
  attendeeIds: string[];
};

type UseAttendeeModalInitializationParams = {
  isOpen: boolean;
  bookingAttendees: Attendee[];
  ownerIdFromBooking: string | null;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
  setSearch: (search: string) => void;
  setAttendees: React.Dispatch<React.SetStateAction<Attendee[]>>;
  setOwnerId: React.Dispatch<React.SetStateAction<string | null>>;
  setInitialSnapshot: React.Dispatch<
    React.SetStateAction<InitialSnapshot | null>
  >;
};

/**
 * Initializes attendee modal state when modal opens
 */
export const useAttendeeModalInitialization = ({
  isOpen,
  bookingAttendees,
  ownerIdFromBooking,
  setMode,
  setSearch,
  setAttendees,
  setOwnerId,
  setInitialSnapshot,
}: UseAttendeeModalInitializationParams) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setMode({ type: "list" });
    setSearch("");

    const nextAttendees = bookingAttendees;
    setAttendees(nextAttendees);

    const ownerWithinAttendees = nextAttendees.find(
      (attendee) => attendee.playerId === ownerIdFromBooking
    );
    const resolvedOwnerId =
      ownerWithinAttendees?.playerId ?? nextAttendees[0]?.playerId ?? null;

    setOwnerId(resolvedOwnerId);
    setInitialSnapshot({
      ownerId: resolvedOwnerId,
      attendeeIds: nextAttendees.map((attendee) => attendee.playerId),
    });
  }, [
    bookingAttendees,
    isOpen,
    ownerIdFromBooking,
    setAttendees,
    setInitialSnapshot,
    setMode,
    setOwnerId,
    setSearch,
  ]);
};

/**
 * Validates that the target attendee still exists when in replace mode.
 * If the target is removed while in replace mode, resets to list mode.
 */
type UseReplaceModeValidationParams = {
  mode: Mode;
  attendees: Attendee[];
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
};

export const useReplaceModeValidation = ({
  mode,
  attendees,
  setMode,
}: UseReplaceModeValidationParams) => {
  useEffect(() => {
    if (mode.type !== "replace") {
      return;
    }
    const targetExists = attendees.some(
      (attendee) => attendee.playerId === mode.targetPlayerId
    );
    if (!targetExists) {
      setMode({ type: "list" });
    }
  }, [attendees, mode, setMode]);
};
